import { supabase } from "@/integrations/supabase/client";
import { getOrCreateSessionId, readSessionId } from "@/lib/academy-session";
import { getSessionProgress, completeSessionLesson } from "@/lib/academy.functions";

export type ProgressIdentity =
  | { userId: string; sessionId: null }
  | { userId: null; sessionId: string };

function identity(): ProgressIdentity {
  // Caller responsible for awaiting auth state if needed.
  // We read the cached session synchronously to avoid extra latency.
  // markLessonComplete() also re-checks getUser() for accuracy.
  return { userId: null, sessionId: getOrCreateSessionId() };
}

async function currentIdentity(): Promise<ProgressIdentity> {
  const { data } = await supabase.auth.getUser();
  if (data.user) return { userId: data.user.id, sessionId: null };
  return { userId: null, sessionId: getOrCreateSessionId() };
}

export async function fetchCompletedLessonIds(
  trackLessonIds: string[],
): Promise<Set<string>> {
  if (trackLessonIds.length === 0) return new Set();
  const id = await currentIdentity();
  if (!id.userId) {
    try {
      const ids = await getSessionProgress({
        data: { sessionId: id.sessionId as string, lessonIds: trackLessonIds },
      });
      return new Set(ids);
    } catch (e) {
      console.error("fetchCompletedLessonIds", e);
      return new Set();
    }
  }
  const query = supabase
    .from("academy_progress")
    .select("lesson_id, completed_at")
    .in("lesson_id", trackLessonIds)
    .not("completed_at", "is", null)
    .eq("user_id", id.userId);
  const { data, error } = await query;
  if (error) {
    console.error("fetchCompletedLessonIds", error);
    return new Set();
  }
  return new Set((data ?? []).map((r) => r.lesson_id));
}

export async function markLessonComplete(lessonId: string): Promise<void> {
  const id = await currentIdentity();
  if (!id.userId) {
    await completeSessionLesson({ data: { sessionId: id.sessionId as string, lessonId } });
    return;
  }
  // Try update first (anon update RLS allows by session_id; user policy allows by user_id)
  const base = supabase.from("academy_progress");
  const matchCol = "user_id" as const;
  const matchVal: string = id.userId;
  const { data: existing } = await base
    .select("id")
    .eq("lesson_id", lessonId)
    .eq(matchCol, matchVal)
    .maybeSingle();
  if (existing) {
    const { error } = await base
      .update({ status: "complete", completed_at: new Date().toISOString() })
      .eq("id", existing.id);
    if (error) throw new Error(error.message);
    return;
  }
  const completed_at = new Date().toISOString();
  const row = { lesson_id: lessonId, user_id: id.userId, status: "complete", completed_at };
  // Supabase typed insert is strict about exact-shape unions; safe at runtime.
  const { error } = await base.insert(row as never);
  if (error) throw new Error(error.message);
}

export function ensureSessionBootstrap(): void {
  // touches the cookie so it exists for the first write
  void identity();
  // exercised for side effect; readSessionId is exported for callers
  readSessionId();
}